import { ChevronLeft } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "~/lib/utils";

/**
 * How many columns the option cards fan out into once there is room.
 *
 * AIDEV-NOTE: Mobile stays a single column deliberately (Adam, 2026-09-09) — the narrow
 * stack is right on a phone. From `md` the overlay is full-screen on a desktop monitor, so
 * a 400px column stranded in the middle of it forced scrolling past acres of empty space.
 * Viewport breakpoints, not container queries: the overlay is portaled to <body> and its
 * width IS the viewport, so there is no parent container to query.
 */
type PickerColumns = "two" | "three" | "sections";

const COLUMN_CLASS: Record<PickerColumns, string> = {
    two: "space-y-3 md:grid md:grid-cols-2 md:gap-4 md:space-y-0",
    three: "space-y-3 md:grid md:grid-cols-2 md:gap-4 md:space-y-0 xl:grid-cols-3",
    // The voice screen groups its cards into labelled sections, so the grid belongs
    // INSIDE each section (see MEDITATION_PICKER_GRID) — not on this container, which
    // would lay the sections themselves side by side.
    sections: "space-y-6 md:space-y-8",
};

const WIDTH_CLASS: Record<PickerColumns, string> = {
    two: "md:max-w-3xl",
    three: "md:max-w-3xl xl:max-w-6xl",
    sections: "md:max-w-3xl",
};

/** The card grid used inside a sectioned picker screen. */
export const MEDITATION_PICKER_GRID = "md:grid md:grid-cols-2 md:gap-4";

interface PickerScreenProps {
    title: string;
    hint: string;
    onBack: () => void;
    children: ReactNode;
    columns?: PickerColumns;
}

export function PickerScreen({
    title,
    hint,
    onBack,
    children,
    columns = "three",
}: PickerScreenProps) {
    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 pb-8 pt-5 sm:px-6">
            <div
                className={cn(
                    "mx-auto flex w-full flex-col",
                    WIDTH_CLASS[columns],
                )}
            >
                <button
                    type="button"
                    onClick={onBack}
                    data-testid="meditation-setup-back"
                    className="-ml-2 inline-flex min-h-11 w-fit items-center gap-1 rounded-pill px-2 text-sm font-medium text-brand-forest-700 hover:bg-brand-forest-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-forest-500"
                >
                    <ChevronLeft className="size-5" />
                    Back
                </button>
                <header className="mt-6">
                    {/* AIDEV-NOTE: display-h1 per DESIGN.md (Playfair 32/40/700, -0.01em).
                        Mirrors ActionScreenShell's title, minus the shared H1's margins.
                        Was `font-serif text-[32px]` with no weight, which rendered Playfair
                        at 400 — the headline read as body copy. No inline size/weight here. */}
                    <h1
                        id="meditation-setup-title"
                        className="font-display text-3xl font-bold leading-[2rem] tracking-[-0.01em] text-brand-forest-800"
                    >
                        {title}
                    </h1>
                    <p className="mt-2 font-body text-base leading-relaxed text-stone-600">
                        {hint}
                    </p>
                </header>
                <div
                    className={cn("mt-6", COLUMN_CLASS[columns])}
                    role="radiogroup"
                >
                    {children}
                </div>
            </div>
        </div>
    );
}
